/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/ShellAssistantLocationRouteMetaSync.tsx
 * PURPOSE: Keep **`routeMeta.route`** and **`activeFilters`** aligned with the router on every navigation so metadata never stays on a stale CRM path after leaving **`/crm`** (#92)
 * DEPENDENCIES: react, react-router-dom, ./ShellAssistantContext
 * PUBLIC_API: ShellAssistantLocationRouteMetaSync
 * SIDE_EFFECTS: **`setRouteMeta`** functional updates
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 57/120
 *
 * RECENT: 2026-06-21 [2.2.1468] — On path change, derive selectedRecord from URL (pages/calendar) instead of clearing before route bridges run.
 * RECENT_PRIOR: 2026-05-12 — Effect deps use **`setRouteMeta`** only (not full context **`value`**); updater **`return prev`** when **`route`** and **`activeFilters`** are unchanged — fixes assistant route-meta update loop (#92).
 */

import { useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { focusEntityHintFromRoute } from '@shared/lib/frontend/utils/assistantRouteFocusHint';
import { ShellAssistantReactContext } from './ShellAssistantContext';

/** Modules where `selectedRecord` is owned by a route bridge (CRM drawer, pages URL, calendar). */
function modulePreservesSelectedRecord(pathname: string): boolean {
  if (pathname === '/crm' || pathname.startsWith('/crm/')) return true;
  if (pathname.includes('/workspaces/') && pathname.includes('/pages')) return true;
  if (pathname === '/calendar' || pathname.startsWith('/calendar/')) return true;
  return false;
}

function searchToFilterRecord(search: string): Record<string, unknown> | null {
  if (!search || search === '?') return null;
  const sp = new URLSearchParams(search);
  const o: Record<string, unknown> = {};
  sp.forEach((v, k) => {
    o[k] = v;
  });
  return Object.keys(o).length ? o : null;
}

export function ShellAssistantLocationRouteMetaSync() {
  const { pathname, search } = useLocation();
  const ctx = useContext(ShellAssistantReactContext);
  // Do not depend on full `ctx` in useEffect: provider `value` is recreated when `routeMeta`
  // updates, which would retrigger this effect and loop. `setRouteMeta` is stable (useCallback).
  const setRouteMeta = ctx?.setRouteMeta;

  const preservesSelection = useMemo(
    () => modulePreservesSelectedRecord(pathname),
    [pathname]
  );

  useEffect(() => {
    if (!setRouteMeta) return;
    const route = `${pathname}${search}`;
    const activeFilters = searchToFilterRecord(search);
    setRouteMeta((prev) => {
      const prevPath = (prev.route.split('?')[0] || '').trim();
      const pathChanged = prevPath !== pathname;
      const nextSelected = !preservesSelection
        ? null
        : pathChanged
          ? (focusEntityHintFromRoute(route)?.entity_id ?? null)
          : prev.selectedRecord;
      // Same `route` implies same `search`; do not compare `activeFilters` by reference —
      // `searchToFilterRecord` allocates a new object each run.
      if (prev.route === route && prev.selectedRecord === nextSelected) {
        return prev;
      }
      return {
        ...prev,
        route,
        activeFilters,
        selectedRecord: nextSelected,
      };
    });
  }, [setRouteMeta, pathname, search, preservesSelection]);

  return null;
}
